import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faSearch, faInbox } from '@fortawesome/free-solid-svg-icons';
import { HeartFilled, RightOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { COLORS } from '../../constants/colors';
import { ROUTES } from '../../constants/routes';
import NotificationBell from '../../components/NotificationBell';
import { generateBreadcrumbs } from '../../utils/breadcrumbUtils';
import { useSearch } from '../../contexts/SearchContext';
import { useStore } from '../../hooks/storeContext';

// Keyframes for loading animations
import { keyframes } from 'styled-components';

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const heartbeatFadeOut = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.25); }
  100% { transform: scale(1); }
`;

const fadeIn = keyframes`
  0% { opacity: 0; transform: scale(0.8); }
  100% { opacity: 1; transform: scale(1); }
`;

// Styled Components
const TopNavContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 60px;
  background: white;
  border-bottom: 1px solid ${COLORS.border};
  padding: 0 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  flex-shrink: 0;
  
  /* Hide on mobile - MobileTopBar handles it */
  @media (max-width: 1024px) {
    display: none;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BreadcrumbContainer = styled.nav`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
`;

const BreadcrumbItem = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${props => props.$isLast ? COLORS.textDark : COLORS.textMedium};
  font-weight: ${props => props.$isLast ? '600' : '400'};
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  transition: color 0.2s ease;
  
  &:hover {
    color: ${props => props.$clickable ? '#1890ff' : props.$isLast ? COLORS.textDark : COLORS.textMedium};
  }
`;

const BreadcrumbSeparator = styled(RightOutlined)`
  font-size: 10px;
  color: ${COLORS.textLight};
`;

const Title = styled.h1`
  margin: 0;
  color: ${COLORS.textDark};
  font-size: 20px;
  font-weight: 600;
  line-height: 1.3;
  letter-spacing: -0.3px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CountTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  background: ${COLORS.backgroundLight};
  border: 1px solid ${COLORS.border};
  border-radius: 16px;
  font-size: 14px;
  font-weight: 500;
  color: ${COLORS.textMedium};
  min-width: 50px;
  justify-content: center;
  
  svg {
    font-size: 12px;
  }
`;

const LoadingSpinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid ${COLORS.border};
  border-top: 2px solid ${COLORS.textMedium};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const HeartLoader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1a1a1a;
  font-size: 16px;
  animation: ${heartbeatFadeOut} 0.4s ease-in-out infinite;
`;

const CountContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  animation: ${fadeIn} 0.4s ease-out forwards;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const QuickCallIcon = styled.span`
  color: #1B5E20;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  font-size: 16px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid ${COLORS.border};
  background: white;
  
  &:hover {
    color: #2E7D32;
    background: rgba(27, 94, 32, 0.08);
    border-color: #2E7D32;
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const SearchButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  min-width: 200px;
  background: ${COLORS.backgroundLight};
  border: 1px solid ${COLORS.border};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${COLORS.textLight};
  font-size: 14px;
  
  &:hover {
    background: white;
    border-color: ${COLORS.textLight};
    color: ${COLORS.textMedium};
  }
  
  .shortcut {
    margin-left: auto;
    padding: 2px 6px;
    background: white;
    border: 1px solid ${COLORS.border};
    border-radius: 4px;
    font-size: 11px;
    font-family: monospace;
  }
`;

/**
 * TopNavBar - Universal top navigation bar for all admin pages
 * 
 * Displays breadcrumbs or page title, count, actions, and notifications
 * in a consistent manner across all pages.
 */
const TopNavBar = ({
  title,
  icon,
  count,
  countIcon,
  loading = false,
  loadingType = 'spinner',
  actions,
  enableNotificationPolling = false,
  onDialerOpen,
  breadcrumbs,
  customBreadcrumbLabel
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setIsOpen } = useSearch();
  const { authStore } = useStore();
  const canMessage = authStore.isAdmin || authStore.hasPermission('messaging');
  
  // Detect OS for shortcut display
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  
  // Auto-generate breadcrumbs if not provided
  const activeBreadcrumbs = useMemo(() => {
    if (breadcrumbs && breadcrumbs.length > 0) {
      return breadcrumbs;
    }
    // Auto-generate from current pathname
    return generateBreadcrumbs(location.pathname, { 
      customLastLabel: customBreadcrumbLabel || title 
    });
  }, [breadcrumbs, location.pathname, customBreadcrumbLabel, title]);
  
  // Show count tag if loading OR if count has a value
  const showCountTag = loading || (count !== undefined && count !== null);

  const handleBreadcrumbClick = (path) => {
    if (path) {
      navigate(path);
    }
  };

  return (
    <TopNavContainer>
      <LeftSection>
        {/* Always render breadcrumbs (auto-generated or provided) */}
        {activeBreadcrumbs && activeBreadcrumbs.length > 0 ? (
          <BreadcrumbContainer>
            {activeBreadcrumbs.map((crumb, index) => {
              const isLast = index === activeBreadcrumbs.length - 1;
              const isFirst = index === 0;
              
              return (
                <React.Fragment key={index}>
                  <BreadcrumbItem 
                    $isLast={isLast}
                    $clickable={!isLast && !!crumb.path}
                    onClick={() => !isLast && handleBreadcrumbClick(crumb.path)}
                  >
                    {isFirst && crumb.icon ? crumb.icon : null}
                    {crumb.label}
                  </BreadcrumbItem>
                  {!isLast && <BreadcrumbSeparator />}
                </React.Fragment>
              );
            })}
          </BreadcrumbContainer>
        ) : title ? (
          <Title>
            {icon && <span style={{ display: 'flex', alignItems: 'center' }}>{icon}</span>}
            {title}
          </Title>
        ) : null}
        
        {showCountTag && (
          <CountTag>
            {loading ? (
              loadingType === 'heart' ? (
                <HeartLoader><HeartFilled /></HeartLoader>
              ) : (
                <LoadingSpinner />
              )
            ) : (
              <CountContent>
                {countIcon}
                {count}
              </CountContent>
            )}
          </CountTag>
        )}
      </LeftSection>
      
      <RightSection>
        {actions && (
          <ActionsWrapper>
            {actions}
          </ActionsWrapper>
        )}
        
        <SearchButton onClick={() => setIsOpen(true)}>
          <FontAwesomeIcon icon={faSearch} />
          <span>Search</span>
          <span className="shortcut">{isMac ? '⌘K' : 'Ctrl+K'}</span>
        </SearchButton>
        
        <Tooltip title="Quick Call">
          <QuickCallIcon 
            onClick={onDialerOpen}
            aria-label="Open quick call dialer"
          >
            <FontAwesomeIcon icon={faPhone} />
          </QuickCallIcon>
        </Tooltip>
        
        {canMessage && (
          <Tooltip title="Inbox">
            <QuickCallIcon 
              onClick={() => navigate(ROUTES.ADMIN_INBOX)}
              aria-label="Open inbox"
            >
              <FontAwesomeIcon icon={faInbox} />
            </QuickCallIcon>
          </Tooltip>
        )}
        
        <NotificationBell enablePolling={enableNotificationPolling} />
      </RightSection>
    </TopNavContainer>
  );
};

export default TopNavBar;
